// Early Bird Event Configuration
// Configuration is fetched from the server and can be managed via admin panel

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

export interface EarlyBirdEventConfig {
  name: string;
  startDate: Date;
  endDate: Date;
  isEnabled: boolean;
  discount: number;
  grantedFeatures: readonly string[];
}

// Default fallback config (used while loading or if fetch fails)
const DEFAULT_CONFIG: EarlyBirdEventConfig = {
  name: 'Early Bird',
  startDate: new Date('2026-01-30T00:00:00Z'),
  endDate: new Date('2026-02-06T00:00:00Z'),
  isEnabled: true,
  discount: 40,
  grantedFeatures: [
    'unlimited_recordings',
    'ai_coach',
    'phoneme_analysis',
    'prosody_analysis',
    'pronunciation_tips',
    'interview_practice',
    'interview_advanced_feedback',
    'interview_unlimited_questions',
    'ielts_practice',
    'ielts_full_duration',
    'ielts_detailed_scoring',
    'ielts_personalized_tips',
  ],
};

// Cached config
let cachedConfig: EarlyBirdEventConfig | null = null;
let fetchPromise: Promise<EarlyBirdEventConfig> | null = null;
let lastFetchTime = 0;
const CACHE_DURATION_MS = 60 * 1000; // 1 minute cache

/**
 * Fetch EarlyBird config from server
 */
export const fetchEarlyBirdConfig = async (): Promise<EarlyBirdEventConfig> => {
  // Return cached config if still valid
  const now = Date.now();
  if (cachedConfig && now - lastFetchTime < CACHE_DURATION_MS) {
    return cachedConfig;
  }

  // If a fetch is already in progress, wait for it
  if (fetchPromise) {
    return fetchPromise;
  }

  fetchPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/early-bird`);
      if (!response.ok) {
        throw new Error('Failed to fetch config');
      }
      const data = await response.json();

      cachedConfig = {
        name: data.name,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        isEnabled: data.isEnabled,
        discount: data.discount,
        grantedFeatures: data.grantedFeatures,
      };
      lastFetchTime = Date.now();

      return cachedConfig;
    } catch (error) {
      console.error('Failed to fetch EarlyBird config:', error);
      // Return default config on error
      return DEFAULT_CONFIG;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};

/**
 * Get cached config synchronously (for immediate use)
 * Falls back to default if not yet fetched
 */
export const getEarlyBirdConfig = (): EarlyBirdEventConfig => {
  return cachedConfig || DEFAULT_CONFIG;
};

/**
 * Clear cached config (useful when admin updates config)
 */
export const clearEarlyBirdConfigCache = (): void => {
  cachedConfig = null;
  lastFetchTime = 0;
};

// Legacy export for backwards compatibility
export const EARLY_BIRD_EVENT = DEFAULT_CONFIG;

/**
 * Check if the Early Bird event is currently active
 */
export const isEarlyBirdActive = (): boolean => {
  const config = getEarlyBirdConfig();
  if (!config.isEnabled) return false;

  const now = new Date();
  return now >= config.startDate && now <= config.endDate;
};

/**
 * Get time remaining until event ends
 * Returns object with days, hours, minutes, seconds
 */
export const getTimeRemaining = (): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
} => {
  const config = getEarlyBirdConfig();
  const now = new Date();
  const total = Math.max(0, config.endDate.getTime() - now.getTime());

  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));

  return { days, hours, minutes, seconds, total };
};

/**
 * Format time remaining as string
 */
export const formatTimeRemaining = (): string => {
  const { days, hours, minutes, seconds } = getTimeRemaining();

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  return `${minutes}m ${seconds}s`;
};
